import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps a one-shot async operation (retry, delete, regenerate, submit) in the busy/error state every
 * button needs, so pages do not repeat the try/catch/finally dance. `run` resolves to the result or
 * `undefined` when the action failed (the failure is exposed through `error`).
 */
export function useAsyncAction<TArgs extends unknown[], TResult>(action: (...args: TArgs) => Promise<TResult>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The latest action is kept in a ref (updated after render) so `run` is stable for callers.
  const actionRef = useRef(action);
  useEffect(() => {
    actionRef.current = action;
  });

  const run = useCallback(async (...args: TArgs): Promise<TResult | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await actionRef.current(...args);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { run, busy, error, clearError };
}
